import React, { useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import styles from './legal.module.scss';

interface PolicyTab {
  id: string;
  title: string;
  path: string;
}

const POLICIES: PolicyTab[] = [
  { id: 'terms', title: 'Terms & Conditions', path: '/terms' },
  { id: 'privacy', title: 'Privacy Policy', path: '/privacy' },
  { id: 'shipping', title: 'Shipping Policy', path: '/shipping' },
  { id: 'returns', title: 'Return & Refund Policy', path: '/returns' },
  { id: 'age-verification', title: 'Age Verification Policy', path: '/age-verification' },
];

export const LegalPage: React.FC<{ activeTab: string }> = ({ activeTab }) => {
  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    const currentPolicy = POLICIES.find(p => p.id === activeTab);
    if (currentPolicy) {
      document.title = `Sommlike - ${currentPolicy.title}`;
    } else {
      document.title = 'Sommlike - Policies';
    }
    window.scrollTo(0, 0);
  }, [activeTab]);

  const handleTabChange = (path: string) => {
    history.push(path);
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'terms':
        return (
          <div className={styles.policyContent}>
            <h1>Terms & Conditions</h1>
            <div className={styles.effectiveDate}>Effective Date: June 28, 2026</div>
            
            <p>Welcome to Sommlike ("Sommlike," "we," "our," or "us"). These Terms & Conditions govern your access to and use of our website located at <a href="https://www.sommlike.com">www.sommlike.com</a>, including all purchases made through our online store.</p>
            <p>By accessing this website or placing an order, you agree to these Terms & Conditions.</p>

            <h2>1. About Sommlike</h2>
            <p>Sommlike is an online wine marketplace and retail wine shop located at:</p>
            <address className={styles.addressBlock}>
              <strong>Sommlike</strong><br />
              407 Main Street<br />
              Ridgefield, CT 06877
            </address>

            <h2>2. Age Requirement</h2>
            <p>You must be 21 years of age or older to purchase wine from Sommlike.</p>
            <p>By placing an order you certify that:</p>
            <ul>
              <li>you are at least 21 years old;</li>
              <li>you are legally permitted to purchase alcohol in your jurisdiction;</li>
              <li>the recipient (if different from the purchaser) is also at least 21 years old.</li>
            </ul>
            <p>Government-issued identification may be required upon delivery.</p>

            <h2>3. Adult Signature Required</h2>
            <p>Federal and state law require that all wine deliveries be accepted by an adult aged 21 or older.</p>
            <p>No packages may be left unattended.</p>
            <p>If delivery cannot be completed because no eligible adult is available, additional shipping or redelivery fees may apply.</p>

            <h2>4. Shipping Restrictions</h2>
            <p>Wine shipments are subject to federal, state and local laws.</p>
            <p>Sommlike currently ships only to states where permitted by law.</p>
            <p>We reserve the right to refuse or cancel any order that cannot legally be shipped.</p>
            <p>Shipping availability may change without notice.</p>

            <h2>5. Product Availability</h2>
            <p>Our inventory changes frequently.</p>
            <p>Products may become unavailable after an order is placed.</p>
            <p>If a wine becomes unavailable we may:</p>
            <ul>
              <li>contact you regarding a substitute,</li>
              <li>issue store credit, or</li>
              <li>provide a full refund.</li>
            </ul>

            <h2>6. Pricing</h2>
            <p>Prices are listed in U.S. Dollars.</p>
            <p>Prices are subject to change without notice.</p>
            <p>Applicable sales taxes and shipping charges are calculated at checkout.</p>

            <h2>7. Payment</h2>
            <p>We accept major credit cards and other approved payment methods displayed during checkout.</p>
            <p>Orders are not considered accepted until payment has been authorized.</p>

            <h2>8. Order Acceptance</h2>
            <p>Sommlike reserves the right to:</p>
            <ul>
              <li>refuse any order;</li>
              <li>limit quantities purchased;</li>
              <li>cancel orders suspected of fraud;</li>
              <li>correct pricing errors;</li>
              <li>cancel orders resulting from technical inaccuracies.</li>
            </ul>

            <h2>9. Returns & Refunds</h2>
            <p>Due to alcohol regulations, wine sales are generally final.</p>
            <p>If your order arrives:</p>
            <ul>
              <li>damaged,</li>
              <li>defective,</li>
              <li>incorrect, or</li>
              <li>spoiled,</li>
            </ul>
            <p>please contact us within 48 hours of delivery at: <a href="mailto:hello@sommlike.com">hello@sommlike.com</a></p>
            <p>Photographs may be required before a replacement or refund can be issued.</p>

            <h2>10. Educational Information</h2>
            <p>Wine descriptions, tasting notes, producer stories, food pairings, and recommendations are provided for educational purposes.</p>
            <p>Taste is subjective.</p>
            <p>Sommlike does not guarantee that individual customers will enjoy any particular wine.</p>

            <h2>11. Responsible Consumption</h2>
            <p>Sommlike promotes the responsible enjoyment of alcoholic beverages.</p>
            <p>Please drink responsibly.</p>
            <p>Never drink and drive.</p>

            <h2>12. Intellectual Property</h2>
            <p>All website content, including but not limited to:</p>
            <ul>
              <li>Sommlike branding</li>
              <li>logos</li>
              <li>graphics</li>
              <li>product photography</li>
              <li>educational materials</li>
              <li>tasting notes</li>
              <li>written content</li>
            </ul>
            <p>is owned by Sommlike or its licensors and may not be copied, reproduced or distributed without written permission.</p>

            <h2>13. User Conduct</h2>
            <p>You agree not to:</p>
            <ul>
              <li>use the website unlawfully;</li>
              <li>interfere with website operations;</li>
              <li>upload malicious software;</li>
              <li>attempt unauthorized access;</li>
              <li>impersonate another person;</li>
              <li>misuse our content.</li>
            </ul>

            <h2>14. Third-Party Links</h2>
            <p>Our website may contain links to third-party websites.</p>
            <p>Sommlike is not responsible for the content, privacy practices, or services offered by third parties.</p>

            <h2>15. Disclaimer</h2>
            <p>The website and all products and services are provided "as is" and "as available."</p>
            <p>To the fullest extent permitted by law, Sommlike disclaims all warranties, express or implied.</p>

            <h2>16. Limitation of Liability</h2>
            <p>To the fullest extent permitted by law, Sommlike shall not be liable for indirect, incidental, consequential, or special damages arising from the use of this website or the purchase of products.</p>
            <p>Our maximum liability shall not exceed the amount paid for the applicable order.</p>

            <h2>17. Privacy</h2>
            <p>Your use of this website is also governed by our Privacy Policy, which explains how we collect and use personal information.</p>

            <h2>18. Changes to These Terms</h2>
            <p>We may update these Terms & Conditions from time to time.</p>
            <p>Changes become effective immediately upon posting.</p>
            <p>Your continued use of the website constitutes acceptance of the revised Terms.</p>

            <h2>19. Governing Law</h2>
            <p>These Terms & Conditions are governed by the laws of the State of Connecticut, without regard to conflict of law principles.</p>

            <h2>20. Contact Us</h2>
            <p>If you have any questions regarding these Terms & Conditions, please contact:</p>
            <address className={styles.addressBlock}>
              <strong>Sommlike</strong><br />
              407 Main Street<br />
              Ridgefield, CT 06877<br />
              Email: <a href="mailto:hello@sommlike.com">hello@sommlike.com</a><br />
              Website: <a href="https://www.sommlike.com">www.sommlike.com</a>
            </address>
          </div>
        );
      case 'privacy':
        return (
          <div className={styles.policyContent}>
            <h1>Privacy Policy</h1>
            <div className={styles.effectiveDate}>Effective Date: June 28, 2026</div>

            <p>At Sommlike ("Sommlike," "we," "our," or "us"), we respect your privacy and are committed to protecting your personal information.</p>
            <p>This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit <a href="https://www.sommlike.com">www.sommlike.com</a>, make a purchase, sign up for our emails, register for events, or otherwise interact with us.</p>
            <p>By using our website, you agree to this Privacy Policy.</p>

            <h2>Information We Collect</h2>
            <p>We may collect information you provide directly to us, including:</p>
            <ul>
              <li>Name</li>
              <li>Billing and shipping address</li>
              <li>Email address</li>
              <li>Phone number</li>
              <li>Date of birth or confirmation that you are 21 years of age or older (when required)</li>
              <li>Payment information (processed securely through our payment providers)</li>
              <li>Purchase history</li>
              <li>Event registrations</li>
              <li>Customer service inquiries</li>
              <li>Email marketing preferences</li>
            </ul>

            <h2>Information Collected Automatically</h2>
            <p>When you visit our website, we may automatically collect certain information, including:</p>
            <ul>
              <li>IP address</li>
              <li>Browser type</li>
              <li>Device information</li>
              <li>Operating system</li>
              <li>Pages viewed</li>
              <li>Time spent on our website</li>
              <li>Referring website</li>
              <li>General location based on IP address</li>
            </ul>
            <p>This information helps us improve our website and customer experience.</p>

            <h2>Cookies & Similar Technologies</h2>
            <p>We use cookies and similar technologies to:</p>
            <ul>
              <li>Remember your preferences</li>
              <li>Maintain your shopping cart</li>
              <li>Improve website functionality</li>
              <li>Analyze website traffic</li>
              <li>Measure marketing performance</li>
              <li>Personalize your shopping experience</li>
            </ul>
            <p>You may disable cookies through your browser settings, although some website features may not function properly.</p>

            <h2>How We Use Your Information</h2>
            <p>We use your information to:</p>
            <ul>
              <li>Process and fulfill orders</li>
              <li>Verify legal age where required</li>
              <li>Process payments</li>
              <li>Deliver products</li>
              <li>Provide customer support</li>
              <li>Send order confirmations and shipping updates</li>
              <li>Communicate regarding events and educational experiences</li>
              <li>Send newsletters and promotional emails (where permitted)</li>
              <li>Improve our website and services</li>
              <li>Detect fraud and protect our business</li>
              <li>Comply with legal obligations</li>
            </ul>

            <h2>Email Marketing</h2>
            <p>If you subscribe to our mailing list, we may send:</p>
            <ul>
              <li>Wine recommendations</li>
              <li>Educational content</li>
              <li>Event invitations</li>
              <li>Promotions</li>
              <li>New product announcements</li>
            </ul>
            <p>You may unsubscribe at any time by clicking the "Unsubscribe" link included in our emails or by contacting us directly.</p>

            <h2>Payment Processing</h2>
            <p>Payments are processed by secure third-party payment processors.</p>
            <p>Sommlike does not store complete credit card numbers on our servers.</p>
            <p>Payment providers maintain their own privacy and security policies.</p>

            <h2>Sharing Your Information</h2>
            <p>We do not sell your personal information.</p>
            <p>We may share information with trusted service providers who help us operate our business, including:</p>
            <ul>
              <li>Payment processors</li>
              <li>Shipping carriers</li>
              <li>Email marketing providers</li>
              <li>Website hosting providers</li>
              <li>Analytics providers</li>
              <li>Technology vendors that support our online store</li>
            </ul>
            <p>These providers may only use your information to provide services on our behalf.</p>
            <p>We may also disclose information when required by law or to protect our legal rights.</p>

            <h2>Data Security</h2>
            <p>We use commercially reasonable administrative, technical, and physical safeguards to protect your personal information.</p>
            <p>However, no method of internet transmission or electronic storage is completely secure, and we cannot guarantee absolute security.</p>

            <h2>Data Retention</h2>
            <p>We retain personal information only for as long as necessary to:</p>
            <ul>
              <li>Complete transactions</li>
              <li>Provide customer service</li>
              <li>Maintain business records</li>
              <li>Comply with legal, accounting, and regulatory requirements</li>
              <li>Resolve disputes and enforce our agreements</li>
            </ul>

            <h2>Your Privacy Choices</h2>
            <p>Depending on where you live, you may have rights regarding your personal information, including the right to:</p>
            <ul>
              <li>Request access to your personal information</li>
              <li>Request correction of inaccurate information</li>
              <li>Request deletion of your personal information (subject to legal exceptions)</li>
              <li>Opt out of marketing communications</li>
            </ul>
            <p>To exercise these rights, contact us using the information below.</p>

            <h2>Children's Privacy</h2>
            <p>Our website is intended only for individuals who are at least 21 years of age.</p>
            <p>We do not knowingly collect personal information from anyone under the age of 21.</p>
            <p>If we become aware that such information has been collected, we will take reasonable steps to delete it.</p>

            <h2>Third-Party Websites</h2>
            <p>Our website may contain links to third-party websites.</p>
            <p>We are not responsible for the privacy practices or content of those websites.</p>

            <h2>Changes to This Privacy Policy</h2>
            <p>We may update this Privacy Policy from time to time.</p>
            <p>Any changes will be posted on this page with an updated Effective Date.</p>

            <h2>Contact Us</h2>
            <p>If you have questions about this Privacy Policy or our privacy practices, please contact us:</p>
            <address className={styles.addressBlock}>
              <strong>Sommlike</strong><br />
              407 Main Street<br />
              Ridgefield, CT 06877<br />
              Email: <a href="mailto:hello@sommlike.com">hello@sommlike.com</a><br />
              Website: <a href="https://www.sommlike.com">www.sommlike.com</a>
            </address>
          </div>
        );
      case 'shipping':
        return (
          <div className={styles.policyContent}>
            <h1>Shipping Policy</h1>
            <div className={styles.effectiveDate}>Effective Date: June 28, 2026</div>

            <p>Thank you for shopping with Sommlike. We are committed to delivering your wines safely, efficiently, and in compliance with all applicable alcohol shipping laws.</p>

            <h2>Order Processing</h2>
            <p>Orders are typically processed within 1–3 business days after payment is received and verified.</p>
            <p>Orders are processed Monday through Friday, excluding holidays.</p>
            <p>You will receive a confirmation email after placing your order and a separate email with tracking information once your order has shipped.</p>

            <h2>Shipping Eligibility</h2>
            <p>Wine shipments are available only to destinations where permitted by applicable federal, state, and local laws.</p>
            <p>We reserve the right to refuse or cancel any order that cannot legally be shipped.</p>
            <p>Shipping availability may change without notice.</p>

            <h2>Age Verification & Adult Signature</h2>
            <p>You must be 21 years of age or older to purchase wine from Sommlike.</p>
            <p>An adult aged 21 or older must be present to receive and sign for all wine deliveries.</p>
            <p>A valid government-issued photo ID may be required upon delivery.</p>
            <p>Carriers cannot leave alcohol packages unattended or deliver them to minors.</p>
            <p>If no eligible adult is available, the carrier may attempt redelivery or return the package to us. Additional shipping or return fees may apply.</p>

            <h2>Shipping Rates</h2>
            <p>Shipping charges are calculated at checkout based on:</p>
            <ul>
              <li>Delivery destination</li>
              <li>Package weight</li>
              <li>Number of bottles ordered</li>
              <li>Selected shipping method</li>
            </ul>
            <p>From time to time, Sommlike may offer promotional or free shipping offers, subject to applicable terms and exclusions.</p>

            <h2>Delivery Times</h2>
            <p>Estimated delivery times begin once your order has shipped and may vary depending on the carrier, destination, weather conditions, and other factors beyond our control.</p>
            <p>Delivery dates are estimates only and are not guaranteed.</p>

            <h2>Weather Holds</h2>
            <p>To help protect wine quality, Sommlike may temporarily delay shipments during periods of extreme heat or cold.</p>
            <p>If your shipment is placed on a weather hold, we will notify you and ship your order when conditions are appropriate, unless you request otherwise and assume the associated risk.</p>

            <h2>Incorrect Shipping Information</h2>
            <p>Please ensure that your shipping address is complete and accurate.</p>
            <p>Sommlike is not responsible for delays, returned shipments, or additional fees resulting from incorrect or incomplete shipping information provided by the customer.</p>

            <h2>Damaged Shipments</h2>
            <p>If your order arrives damaged, please contact us within 48 hours of delivery at <a href="mailto:hello@sommlike.com">hello@sommlike.com</a>.</p>
            <p>Please include:</p>
            <ul>
              <li>Your order number</li>
              <li>Photos of the damaged product(s)</li>
              <li>Photos of the shipping box and packaging</li>
              <li>A brief description of the issue</li>
            </ul>
            <p>We will review your claim and, where appropriate, arrange for a replacement, store credit, or refund.</p>

            <h2>Lost or Delayed Shipments</h2>
            <p>If your shipment appears lost or significantly delayed, please contact us and we will work with the shipping carrier to investigate.</p>
            <p>Sommlike is not responsible for delays caused by weather, carrier disruptions, or other circumstances beyond our control.</p>

            <h2>Returns Due to Failed Delivery</h2>
            <p>If a shipment is returned because:</p>
            <ul>
              <li>No adult (21+) was available to sign,</li>
              <li>Delivery was repeatedly attempted without success,</li>
              <li>The package was refused, or</li>
              <li>The customer provided an incorrect address,</li>
            </ul>
            <p>the customer may be responsible for return shipping charges, restocking fees, and the cost of reshipping the order.</p>

            <h2>In-Store Pickup</h2>
            <p>When available, customers may choose in-store pickup at:</p>
            <address className={styles.addressBlock}>
              <strong>Sommlike</strong><br />
              407 Main Street<br />
              Ridgefield, CT 06877
            </address>
            <p>Customers picking up wine must present a valid government-issued photo ID showing they are at least 21 years of age.</p>

            <h2>Contact Us</h2>
            <p>If you have questions about shipping or your order, please contact us:</p>
            <address className={styles.addressBlock}>
              <strong>Sommlike</strong><br />
              407 Main Street<br />
              Ridgefield, CT 06877<br />
              Email: <a href="mailto:hello@sommlike.com">hello@sommlike.com</a><br />
              Website: <a href="https://www.sommlike.com">www.sommlike.com</a>
            </address>
          </div>
        );
      case 'returns':
        return (
          <div className={styles.policyContent}>
            <h1>Return & Refund Policy</h1>
            <div className={styles.effectiveDate}>Effective Date: June 28, 2026</div>

            <p>At Sommlike, we take great care in selecting, storing, and shipping every bottle. Because wine is a perishable product and its sale is regulated by law, our return policy differs from that of many other retail products.</p>

            <h2>All Wine Sales Are Final</h2>
            <p>Unless otherwise required by law, all wine sales are final. We are unable to accept returns or exchanges for wines that have been delivered as ordered.</p>

            <h2>Damaged or Incorrect Orders</h2>
            <p>If your order arrives:</p>
            <ul>
              <li>Damaged during shipping</li>
              <li>Incorrect</li>
              <li>Missing items</li>
            </ul>
            <p>please contact us within 48 hours of delivery at <a href="mailto:hello@sommlike.com">hello@sommlike.com</a>.</p>
            <p>To help us resolve your claim, please include:</p>
            <ul>
              <li>Your order number</li>
              <li>A description of the issue</li>
              <li>Photos of the damaged bottle(s), shipping box, and packaging (if applicable)</li>
            </ul>
            <p>Once reviewed, we may, at our discretion:</p>
            <ul>
              <li>Replace the product (if available)</li>
              <li>Issue store credit</li>
              <li>Provide a refund to the original payment method</li>
            </ul>

            <h2>Wine Quality Concerns</h2>
            <p>If you believe a bottle is flawed (for example, due to cork taint or another winemaking fault), please contact us within 30 days of purchase.</p>
            <p>We may request:</p>
            <ul>
              <li>The original receipt or order confirmation</li>
              <li>The bottle with its remaining contents</li>
              <li>The original cork or closure (if applicable)</li>
              <li>Additional information regarding the issue</li>
            </ul>
            <p>Each claim is evaluated individually. If we determine the bottle is defective, we may offer a replacement, store credit, or refund at our discretion.</p>

            <h2>Shipping Damage</h2>
            <p>If a package arrives visibly damaged:</p>
            <ul>
              <li>Photograph the package before opening it, if possible.</li>
              <li>Save all original packaging materials.</li>
              <li>Contact us within 48 hours of delivery.</li>
            </ul>
            <p>Failure to report shipping damage promptly may affect our ability to file a claim with the carrier.</p>

            <h2>Failed Deliveries</h2>
            <p>Wine deliveries require the signature of an adult aged 21 years or older.</p>
            <p>If a shipment is returned because:</p>
            <ul>
              <li>No adult was available to sign,</li>
              <li>The delivery address provided was incorrect,</li>
              <li>Delivery was refused, or</li>
              <li>The package was returned by the carrier,</li>
            </ul>
            <p>the customer may be responsible for return shipping charges and any applicable reshipping costs before the order can be resent.</p>

            <h2>Event Tickets</h2>
            <p>Tickets for Sommlike educational tastings, seminars, and special events are generally non-refundable.</p>
            <p>If you are unable to attend, please contact us before the event. When possible, we may allow ticket transfers or issue store credit at our discretion.</p>
            <p>If Sommlike cancels an event, customers will receive either:</p>
            <ul>
              <li>A full refund, or</li>
              <li>The option to transfer their registration to a future event.</li>
            </ul>

            <h2>Gift Cards</h2>
            <p>Sommlike gift cards are non-refundable and cannot be redeemed for cash except where required by law.</p>

            <h2>How Refunds Are Issued</h2>
            <p>Approved refunds will be issued to the original payment method whenever possible.</p>
            <p>Please allow 5–10 business days after the refund is processed for it to appear on your account, depending on your financial institution.</p>

            <h2>Contact Us</h2>
            <p>If you have any questions regarding returns or refunds, please contact us:</p>
            <address className={styles.addressBlock}>
              <strong>Sommlike</strong><br />
              407 Main Street<br />
              Ridgefield, CT 06877<br />
              Email: <a href="mailto:hello@sommlike.com">hello@sommlike.com</a><br />
              Website: <a href="https://www.sommlike.com">www.sommlike.com</a>
            </address>
          </div>
        );
      case 'age-verification':
        return (
          <div className={styles.policyContent}>
            <h1>Age Verification Policy</h1>
            <div className={styles.effectiveDate}>Effective Date: June 28, 2026</div>

            <p>At Sommlike, we are committed to the responsible sale of alcoholic beverages and comply with all applicable federal, state, and local laws governing the sale and delivery of wine.</p>

            <h2>Legal Drinking Age</h2>
            <p>You must be 21 years of age or older to purchase wine or any alcoholic beverage from Sommlike.</p>
            <p>By placing an order through <a href="https://www.sommlike.com">www.sommlike.com</a>, you represent and warrant that:</p>
            <ul>
              <li>You are at least 21 years of age.</li>
              <li>You are legally permitted to purchase alcohol in your jurisdiction.</li>
              <li>Any recipient of your order is also at least 21 years of age.</li>
            </ul>
            <p>Providing false or misleading information regarding your age is prohibited and may violate applicable law.</p>

            <h2>Website Access</h2>
            <p>Our website is intended for adults of legal drinking age. Visitors may be asked to confirm that they are 21 years of age or older before entering the website or purchasing alcoholic beverages.</p>

            <h2>Age Verification During Checkout</h2>
            <p>To help prevent underage purchases, Sommlike may use age verification measures during the checkout process, which may include:</p>
            <ul>
              <li>Confirmation of your date of birth</li>
              <li>Verification of your name and address</li>
              <li>Identity verification through trusted third-party services</li>
              <li>Requests for additional documentation if necessary</li>
            </ul>
            <p>If we are unable to verify your age, we reserve the right to delay or cancel your order.</p>

            <h2>Adult Signature Required</h2>
            <p>All shipments containing alcoholic beverages require the signature of an adult who is 21 years of age or older.</p>
            <p>At the time of delivery:</p>
            <ul>
              <li>The recipient must present a valid government-issued photo ID upon request.</li>
              <li>The delivery carrier may verify the recipient's age before releasing the package.</li>
              <li>Packages containing alcohol cannot be left unattended, delivered to minors, or left without an adult signature.</li>
            </ul>
            <p>If no eligible adult is available to receive the shipment, the carrier may attempt redelivery or return the package to Sommlike. Additional shipping or return fees may apply.</p>

            <h2>In-Store Purchases & Pickup</h2>
            <p>Customers purchasing wine in-store or collecting online orders from our retail location may be required to present a valid government-issued photo ID verifying they are at least 21 years of age.</p>
            <p>Sommlike reserves the right to refuse any sale or pickup if satisfactory proof of age cannot be provided.</p>

            <h2>Refusal of Service</h2>
            <p>Sommlike reserves the right to refuse or cancel any order if:</p>
            <ul>
              <li>We cannot verify the purchaser's age.</li>
              <li>We suspect false or fraudulent information has been provided.</li>
              <li>We believe the purchase may violate applicable law.</li>
              <li>The purchaser or recipient does not meet legal age requirements.</li>
            </ul>

            <h2>Responsible Consumption</h2>
            <p>Sommlike encourages the responsible enjoyment of alcoholic beverages.</p>
            <p>Please drink responsibly and never drink and drive.</p>

            <h2>Questions</h2>
            <p>If you have questions regarding our Age Verification Policy, please contact us:</p>
            <address className={styles.addressBlock}>
              <strong>Sommlike</strong><br />
              407 Main Street<br />
              Ridgefield, CT 06877<br />
              Email: <a href="mailto:hello@sommlike.com">hello@sommlike.com</a><br />
              Website: <a href="https://www.sommlike.com">www.sommlike.com</a>
            </address>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className={styles.legalLayout}>
      {/* Header Banner */}
      <div className={styles.heroBanner}>
        <div className="container text-center">
          <h2>Policies & Terms</h2>
          <p>Please read our terms, conditions, and store policies below.</p>
        </div>
      </div>

      <div className="container py-4 py-md-5">
        <div className="row">
          {/* Sidebar Navigation */}
          <div className="col-lg-3 mb-4 mb-lg-0">
            {/* Desktop Navigation */}
            <div className={`list-group d-none d-lg-block ${styles.sidebarNav}`}>
              {POLICIES.map((policy) => (
                <button
                  key={policy.id}
                  className={`list-group-item list-group-item-action ${
                    activeTab === policy.id ? styles.activeTabItem : ''
                  } ${styles.sidebarItem}`}
                  onClick={() => handleTabChange(policy.path)}
                >
                  {policy.title}
                </button>
              ))}
            </div>

            {/* Mobile Dropdown */}
            <div className="d-block d-lg-none">
              <label htmlFor="policy-select" className={styles.mobileSelectLabel}>
                Select Policy:
              </label>
              <select
                id="policy-select"
                className={`form-control ${styles.mobileSelect}`}
                value={location.pathname}
                onChange={(e) => handleTabChange(e.target.value)}
              >
                {POLICIES.map((policy) => (
                  <option key={policy.id} value={policy.path}>
                    {policy.title}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Main Content */}
          <div className="col-lg-9">
            <div className={styles.contentContainer}>
              {renderContent()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegalPage;
